import React, { useState, useEffect, useRef, useCallback } from "react";
import { CloseIcon } from "./Icons";
import ebay from "../services/ebay";
import amazon from "../services/amazon";
import ebayDescription from "../services/ebay-description";
import { marketplace } from "../services/api";
import { toast } from "./Toast";

const CONDITION_OPTIONS = [
  { value: "new", label: "New" },
  { value: "like_new", label: "Like New / Open Box" },
  { value: "good", label: "Used - Good" },
  { value: "fair", label: "Used - Acceptable" },
  { value: "poor", label: "For Parts / Not Working" },
];

/**
 * Read the listing_data JSON blob off a marketplace record.
 *
 * Tolerates a double-encoded value, which older records may carry.
 */
function parseListingData(raw) {
  if (!raw) return {};
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return typeof parsed === "string" ? JSON.parse(parsed) : parsed || {};
  } catch (err) {
    return {};
  }
}

/**
 * Modal for listing or editing a product on eBay.
 *
 * Products synced from Amazon are prefilled from their Amazon details and
 * photos, with the description built from the eBay description template.
 */
function EbayListingModal({
  product,
  imageUrl,
  onClose,
  onSuccess,
  existingListing = null,
}) {
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [condition, setCondition] = useState("new");
  // Package weight, needed when the fulfillment policy uses calculated
  // shipping. Defaults to the last-used weight so repeat listings are quick.
  const [weightLbs, setWeightLbs] = useState(
    () => localStorage.getItem("ebay_last_weight_lbs") || "",
  );
  const [weightOz, setWeightOz] = useState(
    () => localStorage.getItem("ebay_last_weight_oz") || "",
  );
  // eBay requires the Brand aspect in most categories; "Unbranded" is its
  // accepted value for generic items.
  const [brand, setBrand] = useState("Unbranded");
  const [categoryId, setCategoryId] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  // Item specifics the chosen category requires beyond Brand/MPN (e.g. Type,
  // Size) — fetched from eBay's taxonomy, values keyed by aspect name.
  const [requiredAspects, setRequiredAspects] = useState([]);
  const [aspectValues, setAspectValues] = useState({});
  const [isLoadingAspects, setIsLoadingAspects] = useState(false);
  const [description, setDescription] = useState("");
  const [descriptionEdited, setDescriptionEdited] = useState(false);
  const [showDescriptionSource, setShowDescriptionSource] = useState(false);
  const [images, setImages] = useState([]);
  const [bestOffer, setBestOffer] = useState(false);
  const [autoAcceptPrice, setAutoAcceptPrice] = useState("");
  const [autoDeclinePrice, setAutoDeclinePrice] = useState("");
  const [details, setDetails] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCategory, setIsLoadingCategory] = useState(false);
  const [error, setError] = useState(null);
  const [listingRecord, setListingRecord] = useState(existingListing);

  const mouseDownTargetRef = useRef(null);
  const isEditMode = !!listingRecord;
  const missingSetup = ebay.getMissingRequirements();

  const handleOverlayMouseDown = (e) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e) => {
    if (
      e.target === e.currentTarget &&
      mouseDownTargetRef.current === e.target
    ) {
      // Don't dismiss mid-publish — the user would never learn the outcome.
      if (isSubmitting) return;
      onClose();
    }
  };

  // Load the existing listing record and build the prefill from the product.
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      let record = existingListing;

      if (!record && product?.id) {
        try {
          const response = await marketplace.getByEntityAndMarketplace(
            product.id,
            "ebay",
          );
          record = response.data?.data || null;
        } catch (err) {
          // No existing listing — this is a fresh one.
        }
      }

      const listingDetails = await amazon.buildListingDetails(product, {
        fallbackImageUrl: imageUrl,
      });

      if (cancelled) return;

      setDetails(listingDetails);
      setImages(listingDetails.images);
      setListingRecord(record);

      if (record) {
        setTitle(record.title || listingDetails.title);
        setPrice(
          record.price != null
            ? String(record.price)
            : String(listingDetails.price),
        );
        setCategoryId(record.category_id || "");
        setCategoryName(record.category_name || "");
        setCondition(record.condition || listingDetails.condition);
        const stored = parseListingData(record.listing_data);
        if (stored.quantity) setQuantity(stored.quantity);
        if (stored.weightLbs != null) setWeightLbs(String(stored.weightLbs));
        if (stored.weightOz != null) setWeightOz(String(stored.weightOz));
        setBrand(stored.brand || listingDetails.brand || "Unbranded");
        setAspectValues(stored.aspects || {});
        setBestOffer(!!stored.bestOffer);
        setAutoAcceptPrice(stored.autoAcceptPrice || "");
        setAutoDeclinePrice(stored.autoDeclinePrice || "");
        if (record.description) {
          setDescription(record.description);
          setDescriptionEdited(true);
        } else {
          setDescription(ebayDescription.renderDescription(listingDetails));
        }
      } else {
        setTitle(listingDetails.title);
        setPrice(listingDetails.price ? String(listingDetails.price) : "");
        setCondition(listingDetails.condition);
        setBrand(listingDetails.brand || "Unbranded");
        setDescription(ebayDescription.renderDescription(listingDetails));
      }

      setIsLoading(false);
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [product, imageUrl, existingListing]);

  // Keep the generated description in sync until the user edits it by hand.
  useEffect(() => {
    if (isLoading || descriptionEdited || !details) return;

    setDescription(
      ebayDescription.renderDescription({
        ...details,
        title,
        condition,
        price,
        images,
        eyebrow: categoryName,
      }),
    );
  }, [
    title,
    condition,
    price,
    images,
    categoryName,
    details,
    descriptionEdited,
    isLoading,
  ]);

  // Ask eBay to categorise the title (debounced), for new listings only.
  useEffect(() => {
    if (isLoading || isEditMode || categoryId || !title) return;
    if (!ebay.isConfigured()) return;

    setIsLoadingCategory(true);
    const timeoutId = setTimeout(() => {
      ebay
        .getCategorySuggestions(title)
        .then((suggestions) => {
          setCategorySuggestions(suggestions);
          if (suggestions[0]) {
            setCategoryId(suggestions[0].categoryId);
            setCategoryName(suggestions[0].categoryName);
          }
        })
        .catch((err) => {
          console.log("eBay category suggestion failed:", err.message);
        })
        .finally(() => setIsLoadingCategory(false));
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [title, categoryId, isEditMode, isLoading]);

  // Fetch the item specifics the chosen category requires, so publish can't
  // fail with "The item specific X is missing". Brand and MPN are excluded —
  // they have dedicated fields.
  useEffect(() => {
    if (isLoading || !categoryId || !/^\d+$/.test(categoryId)) {
      setRequiredAspects([]);
      return;
    }
    if (!ebay.isConfigured()) return;

    let cancelled = false;
    setIsLoadingAspects(true);
    const timeoutId = setTimeout(() => {
      ebay
        .getRequiredAspects(categoryId)
        .then((aspects) => {
          if (cancelled) return;
          setRequiredAspects(
            aspects.filter((a) => a.name !== "Brand" && a.name !== "MPN"),
          );
        })
        .catch((err) => {
          // Non-fatal: eBay reports any truly missing aspect at publish time.
          console.log("eBay required aspects lookup failed:", err.message);
        })
        .finally(() => {
          if (!cancelled) setIsLoadingAspects(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [categoryId, isLoading]);

  const handleFindCategories = async () => {
    if (!title) {
      setError("Enter a title first");
      return;
    }
    setIsLoadingCategory(true);
    setError(null);
    try {
      const suggestions = await ebay.getCategorySuggestions(title);
      setCategorySuggestions(suggestions);
      if (suggestions.length > 0) {
        setCategoryId(suggestions[0].categoryId);
        setCategoryName(suggestions[0].categoryName);
      } else {
        setError(
          "eBay returned no category suggestions for this title. Sandbox taxonomy is often incomplete — enter a category ID by hand to continue.",
        );
      }
    } catch (err) {
      setError(`Category lookup failed: ${err.message}`);
    } finally {
      setIsLoadingCategory(false);
    }
  };

  const handleRegenerateDescription = () => {
    if (!details) return;
    setDescription(
      ebayDescription.renderDescription({
        ...details,
        title,
        condition,
        price,
        images,
        eyebrow: categoryName,
      }),
    );
    setDescriptionEdited(false);
  };

  const roundPrice = useCallback((value, roundTo) => {
    if (!value || isNaN(value)) return "";
    return (Math.round(parseFloat(value) / roundTo) * roundTo).toFixed(2);
  }, []);

  const handleImageRemove = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Aspect names/values as eBay expects them: { Type: ["Body Wash"] }.
      const aspects = {};
      for (const [name, value] of Object.entries(aspectValues)) {
        const trimmed = String(value || "").trim();
        if (trimmed) aspects[name] = [trimmed];
      }

      const result = await ebay.postProduct(product, {
        title,
        price: parseFloat(price),
        description,
        categoryId,
        condition,
        images,
        quantity: parseInt(quantity, 10) || 1,
        brand: brand.trim() || "Unbranded",
        mpn: details?.sku || undefined,
        aspects,
        weightLbs: weightLbs || undefined,
        weightOz: weightOz || undefined,
        bestOffer,
        autoAcceptPrice: autoAcceptPrice || undefined,
        autoDeclinePrice: autoDeclinePrice || undefined,
      });

      // Remember the weight for the next listing.
      localStorage.setItem("ebay_last_weight_lbs", weightLbs || "");
      localStorage.setItem("ebay_last_weight_oz", weightOz || "");

      const record = {
        entity_id: product.id,
        marketplace: "ebay",
        external_id: result.listingId,
        external_url: result.listingUrl,
        status: "active",
        title,
        description,
        price: parseFloat(price),
        category_id: categoryId,
        category_name: categoryName,
        condition,
        // Sent as an object — both the create and update routes stringify it.
        listing_data: {
          offerId: result.offerId,
          sku: result.sku,
          brand: brand.trim() || "Unbranded",
          aspects: aspectValues,
          quantity: parseInt(quantity, 10) || 1,
          weightLbs: weightLbs || null,
          weightOz: weightOz || null,
          bestOffer,
          autoAcceptPrice: autoAcceptPrice || null,
          autoDeclinePrice: autoDeclinePrice || null,
        },
      };

      // The listing is live on eBay at this point — a record-save failure
      // must not read as a listing failure. postProduct is SKU-idempotent,
      // so submitting again retries the save without duplicating the listing.
      try {
        if (isEditMode) {
          await marketplace.update(listingRecord.id, record);
        } else {
          await marketplace.create(record);
        }
      } catch (recordErr) {
        console.error("eBay listing published but record save failed:", recordErr);
        setError(
          "Published to eBay, but saving the local record failed — submit again to retry (the eBay listing won't be duplicated).",
        );
        return;
      }

      if (result.listingUrl) {
        toast.success(isEditMode ? "eBay listing updated" : "Listed on eBay", {
          duration: 8000,
          action: { label: "View listing", url: result.listingUrl },
        });
      }
      onSuccess?.(result);
      onClose();
    } catch (err) {
      console.error("Failed to list on eBay:", err);
      setError(err.message || "Failed to list on eBay");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEndListing = async () => {
    if (!window.confirm("End this eBay listing?")) return;

    setIsSubmitting(true);
    setError(null);
    try {
      // Fall back to looking the offer up by SKU, so a missing or malformed
      // listing_data can't leave the listing live on eBay.
      let offerId = parseListingData(listingRecord.listing_data).offerId;
      if (!offerId) {
        const offer = await ebay.getOfferBySku(ebay.buildSku(product));
        offerId = offer?.offerId;
      }

      if (!offerId) {
        throw new Error(
          "Couldn't find the eBay offer for this product — end it from eBay directly.",
        );
      }

      await ebay.endListing(offerId);
      await marketplace.delete(listingRecord.id);
      onSuccess?.(null);
      onClose();
    } catch (err) {
      console.error("Failed to end eBay listing:", err);
      setError(err.message || "Failed to end listing");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="modal-overlay"
        onMouseDown={handleOverlayMouseDown}
        onClick={handleOverlayClick}
      >
        <div
          className="modal"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: "600px" }}
        >
          <div
            className="modal-body"
            style={{ textAlign: "center", padding: "2rem" }}
          >
            <div className="spinner" style={{ margin: "0 auto" }}></div>
            <p style={{ marginTop: "1rem", color: "var(--text-muted)" }}>
              Loading...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Setup gate — eBay needs OAuth credentials and business policies, both of
  // which are configured on the Settings page.
  if (missingSetup.length > 0) {
    return (
      <div
        className="modal-overlay"
        onMouseDown={handleOverlayMouseDown}
        onClick={handleOverlayClick}
      >
        <div
          className="modal"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: "500px" }}
        >
          <div className="modal-header">
            <h2>Configure eBay</h2>
            <button onClick={onClose} className="btn btn-ghost btn-icon">
              <CloseIcon size={20} />
            </button>
          </div>
          <div className="modal-body">
            <p style={{ color: "var(--text-secondary)" }}>
              eBay listing isn't set up yet. Finish the eBay Integration section
              in Settings first.
            </p>
            <div
              style={{
                marginTop: "var(--spacing-md)",
                padding: "var(--spacing-md)",
                background: "var(--bg-secondary)",
                borderRadius: "var(--radius-md)",
                fontSize: "0.875rem",
              }}
            >
              <strong>Still needed:</strong>
              <ul
                style={{
                  margin: "var(--spacing-sm) 0 0 var(--spacing-md)",
                  padding: 0,
                }}
              >
                {missingSetup.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="modal-footer">
            <button onClick={onClose} className="btn btn-secondary">
              Close
            </button>
            <a href="/settings" className="btn btn-primary">
              Open Settings
            </a>
          </div>
        </div>
      </div>
    );
  }

  const titleLength = title.length;
  const titleOverLimit = titleLength > amazon.EBAY_TITLE_MAX;

  return (
    <div
      className="modal-overlay"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "640px" }}
      >
        <div className="modal-header">
          <h2>{isEditMode ? "Update eBay Listing" : "List on eBay"}</h2>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon"
            disabled={isSubmitting}
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {details?.fromAmazon && (
              <div
                style={{
                  padding: "var(--spacing-sm) var(--spacing-md)",
                  background: "rgba(255, 153, 0, 0.12)",
                  borderRadius: "var(--radius-sm)",
                  marginBottom: "var(--spacing-md)",
                  fontSize: "0.875rem",
                }}
              >
                <strong>Synced from Amazon</strong> — title, photos
                {details.sku ? ` and ASIN ${details.sku}` : ""} were pulled from
                the Amazon import.
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: "var(--spacing-sm) var(--spacing-md)",
                  background: "var(--danger-bg)",
                  color: "var(--danger)",
                  borderRadius: "var(--radius-sm)",
                  marginBottom: "var(--spacing-md)",
                }}
              >
                {error}
              </div>
            )}

            <label>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              required
            />
            <small
              style={{
                color: titleOverLimit ? "var(--danger)" : "var(--text-muted)",
              }}
            >
              {titleLength}/{amazon.EBAY_TITLE_MAX} characters
              {titleOverLimit &&
                " — eBay will reject titles over 80 characters"}
            </small>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "var(--spacing-sm)",
                marginTop: "var(--spacing-md)",
              }}
            >
              <div>
                <label>Price</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  required
                />
                <div style={{ display: "flex", gap: "4px", marginTop: "4px" }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPrice(roundPrice(price, 5))}
                  >
                    ~$5
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPrice(roundPrice(price, 10))}
                  >
                    ~$10
                  </button>
                </div>
              </div>
              <div>
                <label>Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
              <div>
                <label>Condition</label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                >
                  {CONDITION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: "var(--spacing-md)" }}>
              <label>Package weight</label>
              <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="lbs"
                  value={weightLbs}
                  onChange={(e) => setWeightLbs(e.target.value)}
                  style={{ width: "80px" }}
                />
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="oz"
                  value={weightOz}
                  onChange={(e) => setWeightOz(e.target.value)}
                  style={{ width: "80px" }}
                />
              </div>
              <small style={{ color: "var(--text-muted)" }}>
                Required by eBay when the shipping policy uses calculated
                rates. Remembered for the next listing.
              </small>
            </div>

            <div style={{ marginTop: "var(--spacing-md)" }}>
              <label>Brand</label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                required
              />
              <small style={{ color: "var(--text-muted)" }}>
                Required by eBay in most categories — use "Unbranded" for
                generic items.
              </small>
            </div>

            <div style={{ marginTop: "var(--spacing-md)" }}>
              <label>
                Category{" "}
                {isLoadingCategory && (
                  <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
                    — finding suggestions…
                  </span>
                )}
              </label>
              <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
                <input
                  type="text"
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setCategoryName("");
                  }}
                  placeholder="eBay category ID"
                  required
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleFindCategories}
                  disabled={isLoadingCategory}
                >
                  Suggest
                </button>
              </div>
              {categoryName ? (
                <small style={{ color: "var(--text-muted)" }}>
                  {categoryName}
                </small>
              ) : (
                <small style={{ color: "var(--text-muted)" }}>
                  Must be a leaf category. If Suggest returns nothing, find an
                  ID with eBay's category lookup and paste it here.
                </small>
              )}
              {categorySuggestions.length > 1 && (
                <div style={{ marginTop: "var(--spacing-sm)" }}>
                  {categorySuggestions.slice(0, 5).map((suggestion) => (
                    <button
                      key={suggestion.categoryId}
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setCategoryId(suggestion.categoryId);
                        setCategoryName(suggestion.categoryName);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        fontWeight:
                          suggestion.categoryId === categoryId ? 600 : 400,
                      }}
                    >
                      {suggestion.categoryPath || suggestion.categoryName}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {(requiredAspects.length > 0 || isLoadingAspects) && (
              <div style={{ marginTop: "var(--spacing-md)" }}>
                <label>
                  Required item specifics{" "}
                  {isLoadingAspects && (
                    <span
                      style={{ color: "var(--text-muted)", fontWeight: 400 }}
                    >
                      — checking category…
                    </span>
                  )}
                </label>
                {requiredAspects.length > 0 && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "var(--spacing-sm)",
                    }}
                  >
                    {requiredAspects.map((aspect) => (
                      <div key={aspect.name}>
                        <label style={{ fontWeight: 400 }}>{aspect.name}</label>
                        <input
                          type="text"
                          list={`ebay-aspect-${aspect.name}`}
                          value={aspectValues[aspect.name] || ""}
                          onChange={(e) =>
                            setAspectValues((prev) => ({
                              ...prev,
                              [aspect.name]: e.target.value,
                            }))
                          }
                          required
                        />
                        {aspect.values.length > 0 && (
                          <datalist id={`ebay-aspect-${aspect.name}`}>
                            {aspect.values.slice(0, 100).map((value) => (
                              <option key={value} value={value} />
                            ))}
                          </datalist>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <small style={{ color: "var(--text-muted)" }}>
                  eBay requires these for the chosen category — publishing
                  fails without them.
                </small>
              </div>
            )}

            <div style={{ marginTop: "var(--spacing-md)" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--spacing-sm)",
                  margin: 0,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={bestOffer}
                  onChange={(e) => setBestOffer(e.target.checked)}
                  style={{ width: "auto", margin: 0 }}
                />
                Accept Best Offers
              </label>

              {bestOffer && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "var(--spacing-sm)",
                    marginTop: "var(--spacing-sm)",
                  }}
                >
                  <div>
                    <label>Auto-accept at or above</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={autoAcceptPrice}
                      onChange={(e) => setAutoAcceptPrice(e.target.value)}
                      placeholder="optional"
                    />
                  </div>
                  <div>
                    <label>Auto-decline below</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={autoDeclinePrice}
                      onChange={(e) => setAutoDeclinePrice(e.target.value)}
                      placeholder="optional"
                    />
                  </div>
                </div>
              )}
              <small style={{ color: "var(--text-muted)" }}>
                Free shipping is set on your shipping policy in Settings — it
                applies to every eBay listing.
              </small>
            </div>

            <div style={{ marginTop: "var(--spacing-md)" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <label style={{ margin: 0 }}>Description</label>
                <div style={{ display: "flex", gap: "4px" }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowDescriptionSource((prev) => !prev)}
                  >
                    {showDescriptionSource ? "Preview" : "Edit HTML"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={handleRegenerateDescription}
                    disabled={!descriptionEdited}
                  >
                    Regenerate
                  </button>
                </div>
              </div>

              {showDescriptionSource ? (
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setDescriptionEdited(true);
                  }}
                  rows={12}
                  style={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                />
              ) : (
                <div
                  style={{
                    border: "1px solid var(--border-color)",
                    borderRadius: "var(--radius-sm)",
                    padding: "var(--spacing-md)",
                    maxHeight: "320px",
                    overflowY: "auto",
                    background: "#fff",
                    color: "#1a1a1a",
                  }}
                  // Preview of the description we generated from our own
                  // template and the product's stored fields.
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
              <small style={{ color: "var(--text-muted)" }}>
                Built from the eBay description template — edit it in Settings.
              </small>
            </div>

            <div style={{ marginTop: "var(--spacing-md)" }}>
              <label>Photos ({images.length})</label>
              {images.length === 0 ? (
                <div style={{ color: "var(--danger)", fontSize: "0.875rem" }}>
                  eBay requires at least one photo.
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "var(--spacing-sm)",
                  }}
                >
                  {images.map((url, index) => (
                    <div key={url} style={{ position: "relative" }}>
                      <img
                        src={url}
                        alt=""
                        style={{
                          width: "72px",
                          height: "72px",
                          objectFit: "cover",
                          borderRadius: "var(--radius-sm)",
                          border:
                            index === 0
                              ? "2px solid var(--primary)"
                              : "1px solid var(--border-color)",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleImageRemove(index)}
                        className="btn btn-ghost btn-icon"
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: "-6px",
                        }}
                        title="Remove photo"
                      >
                        <CloseIcon size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <small style={{ color: "var(--text-muted)" }}>
                The first photo becomes the eBay gallery image.
              </small>
            </div>

            {isEditMode && listingRecord?.external_url && (
              <a
                href={listingRecord.external_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  marginTop: "var(--spacing-md)",
                  color: "var(--accent-primary)",
                  fontSize: "0.875rem",
                }}
              >
                View on eBay →
              </a>
            )}
          </div>

          <div className="modal-footer">
            {isEditMode && (
              <button
                type="button"
                onClick={handleEndListing}
                className="btn btn-danger"
                disabled={isSubmitting}
                style={{ marginRight: "auto" }}
              >
                End Listing
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || images.length === 0 || titleOverLimit}
            >
              {isSubmitting
                ? isEditMode
                  ? "Updating…"
                  : "Publishing…"
                : isEditMode
                  ? "Update Listing"
                  : "Publish to eBay"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EbayListingModal;
